/* ==========================================================================
   plan-clock.js — THE single date engine for every Layby USA surface.

   Extracted verbatim out of occasions.js (2026-09-04) so the homepage hero,
   the occasion tiles, the pace instrument and the fee ledger all answer the
   same question the same way. Before this there were two copies of the maths
   — one here, one inline in sections/main-cart.liquid — and two copies of a
   schedule rule is how a storefront starts quoting a date the checkout will
   not honour.

   The rules, in one place:

     first payment  = the next payable weekday on or after today (Wed by
                      default; the account also allows Thu and Fri)
     payments       = ceil(price / weekly), one per cadence period
     ships          = the ship window counted forward from the FINAL payment
                      ("20 business days" skips weekends; "3 weeks" is calendar)
     lands          = ships + a transit allowance, which is the carrier's, not
                      ours — never quote it as a delivery promise

   Nothing in here is a promise about delivery. The obligation is DISPATCH
   within the ship window; callers say "lands" or "ships", never "arrives by".

   Loaded with `defer` ahead of its consumers. It is idempotent — two sections
   may both request it and the second execution is a no-op.
   ========================================================================== */
(function () {
  'use strict';

  if (window.LaybyClock) return;

  var DAY_MS = 86400000;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var PAYDAY = 3; // Wednesday — the earliest payable day at checkout

  function today() { var d = new Date(); d.setHours(0, 0, 0, 0); return d; }
  function addDays(d, n) { var x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; }

  /** Calendar days forward (or back) skipping Saturday and Sunday. */
  function addBusinessDays(d, n) {
    var x = new Date(d.getTime()), left = Math.abs(n), step = n < 0 ? -1 : 1;
    while (left > 0) {
      x = addDays(x, step);
      var g = x.getDay();
      if (g !== 0 && g !== 6) left--;
    }
    return x;
  }

  /**
   * The next payable day on or after `from`. Returns `from` itself when it
   * already falls on the payday, which is what checkout does.
   */
  function nextPayday(from, dow) {
    var want = typeof dow === 'number' ? dow : PAYDAY;
    var x = new Date(from.getTime());
    while (x.getDay() !== want) x = addDays(x, 1);
    return x;
  }

  /* Day-month order. The plan builder passes the store's setting through
     setDateOrder (data-date-order on its root); with no caller, any non-USD
     store is assumed to write dates day-first. */
  var DATE_ORDER = (function () {
    try { var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active; return cur && cur !== 'USD' ? 'DMY' : 'MDY'; } catch (e) { return 'MDY'; }
  })();
  function setDateOrder(order) { DATE_ORDER = order === 'DMY' ? 'DMY' : 'MDY'; }
  function fmt(d) { return DATE_ORDER === 'DMY' ? d.getDate() + ' ' + MONTHS[d.getMonth()] : MONTHS[d.getMonth()] + ' ' + d.getDate(); }
  function fmtLong(d) { return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear(); }
  function iso(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function money(n) { return '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  /** The nth given weekday of a month — Mother's Day, Thanksgiving and friends. */
  function nthWeekday(year, month, weekday, nth) {
    var d = new Date(year, month, 1);
    while (d.getDay() !== weekday) d = addDays(d, 1);
    return addDays(d, (nth - 1) * 7);
  }

  /**
   * The ship window as something that can be counted forward or back.
   * Understands "20 business days", "1–3 weeks" (far end), "3 weeks", "10 days".
   * Falls back to 20 business days, which is the contractual window — an
   * unparseable setting must never shorten what we appear to promise.
   */
  function parseShipWindow(text) {
    var w = String(text || ''), m;
    if ((m = /(\d+)\s*business/i.exec(w))) return { business: true, n: +m[1] };
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*weeks?/i.exec(w))) return { business: false, n: +m[2] * 7 };
    if ((m = /(\d+)\s*weeks?/i.exec(w))) return { business: false, n: +m[1] * 7 };
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*days?/i.exec(w))) return { business: false, n: +m[2] };
    if ((m = /(\d+)\s*days?/i.exec(w))) return { business: false, n: +m[1] };
    return { business: true, n: 20 };
  }
  function shipForward(d, w) { return w.business ? addBusinessDays(d, w.n) : addDays(d, w.n); }
  function shipBackward(d, w) { return w.business ? addBusinessDays(d, -w.n) : addDays(d, -w.n); }

  /**
   * A whole plan for `price`, in dollars.
   *
   * opts.weekly        instalment in dollars (required)
   * opts.ship          a parsed ship window from parseShipWindow()
   * opts.payday        0-6, default Wednesday
   * opts.cadenceWeeks  1 = weekly, 2 = every two weeks. Default 1.
   * opts.breaks        skipped payments to add to the term. Default 0.
   * opts.from          the day the plan starts from. Default today.
   *
   * Returns { payments, first, last, ship, weeks } where `weeks` is the span
   * in weeks from the first payment to the last, so a caller can compare it
   * against the change ceiling without redoing the arithmetic.
   */
  function plan(price, opts) {
    var o = opts || {};
    var weekly = o.weekly;
    var cadence = o.cadenceWeeks || 1;
    var from = o.from || today();
    var first = nextPayday(from, o.payday);
    var payments = Math.max(1, Math.ceil(price / weekly));
    var stepDays = 7 * cadence;
    var last = addDays(first, (payments - 1) * stepDays + (o.breaks || 0) * stepDays);
    return {
      payments: payments,
      first: first,
      last: last,
      ship: shipForward(last, o.ship || parseShipWindow(null)),
      weeks: Math.round((last - first) / (7 * DAY_MS))
    };
  }

  /**
   * The biggest plan started today whose order still lands before `occasion`.
   * Counts the transit allowance and the ship window backwards from the date,
   * then fits whole payments into what is left. Returns dollars, 0 if nothing
   * fits — callers must handle 0 rather than printing "up to $0".
   */
  function capBefore(occasion, opts) {
    var o = opts || {};
    var weekly = o.weekly;
    var ship = o.ship || parseShipWindow(null);
    var transit = typeof o.transitDays === 'number' ? o.transitDays : 5;
    var first = nextPayday(o.from || today(), o.payday);
    var deadline = shipBackward(addDays(occasion, -transit), ship);
    if (deadline < first) return 0;
    return (Math.floor((deadline - first) / (7 * DAY_MS)) + 1) * weekly;
  }

  function weeksAway(d, from) {
    return Math.max(0, Math.round((d - (from || today())) / (7 * DAY_MS)));
  }

  /** The plan JSON snippets/layaway-plan.liquid renders as `schedule-data`. */
  function readPlan(root) {
    var node = (root || document).querySelector('script[data-layaway-plan]');
    if (!node) return {};
    try { return JSON.parse(node.textContent); } catch (e) { return {}; }
  }

  window.LaybyClock = {
    DAY_MS: DAY_MS,
    MONTHS: MONTHS,
    setDateOrder: setDateOrder,
    DAYS: DAYS,
    PAYDAY: PAYDAY,
    today: today,
    addDays: addDays,
    addBusinessDays: addBusinessDays,
    nextPayday: nextPayday,
    fmt: fmt,
    fmtLong: fmtLong,
    iso: iso,
    money: money,
    nthWeekday: nthWeekday,
    parseShipWindow: parseShipWindow,
    shipForward: shipForward,
    shipBackward: shipBackward,
    plan: plan,
    capBefore: capBefore,
    weeksAway: weeksAway,
    readPlan: readPlan
  };
})();
